import { UseMutateFunction, useMutation } from '@tanstack/react-query';

import { useAppDispatch } from '@store/store';

import { setSnackbar } from '@slices/common';

import { getKpiAlertsHistoryExport, getKpiAlertsHistoryExportForRan } from '@api/kpiAlerts';

import { isEmpty, normalizeObject } from '@utils/common';
import { startOfDayInUTC } from '@utils/kpi-utils/date';

import { IKpiAlertExportDTO, IKpiAlertHistoryExportDTO } from '@interfaces/kpi/dto/kpiAlertsDTO';

import { ESnackbarTypes, EVendorsValues, TExportFileTypes } from '@enums/common';

import { KPI_NOTIFICATIONS_HISTORY_EXPORT } from '@constants/queryKeys';
import { responseMessages } from '@constants/responseMessages';

export interface IUseExportKpiNotificationsHistoryResponse {
	exportKpiAlertsHistory: UseMutateFunction<Promise<void>, unknown, string, IKpiAlertExportDTO>;
	isLoading: boolean;
}

export function useExportKpiAlertsHistory(
	params: IKpiAlertHistoryExportDTO,
): IUseExportKpiNotificationsHistoryResponse {
	const dispatch = useAppDispatch();

	const isOranVendor = params.vendor === EVendorsValues.oran;

	const normalizedParams = normalizeObject(params);

	const requestParams = {
		...normalizedParams,
		...(!isEmpty(normalizedParams.filter) && {
			filter: {
				...normalizedParams.filter,
				from: params.filter?.from ? startOfDayInUTC(new Date(params.filter?.from)) : undefined,
			},
		}),
	};

	const exportFn = isOranVendor
		? getKpiAlertsHistoryExportForRan
		: getKpiAlertsHistoryExport;

	const {
		mutate,
		isLoading,
	} = useMutation<Promise<void>, unknown, string, IKpiAlertExportDTO>({
		mutationKey: [
			KPI_NOTIFICATIONS_HISTORY_EXPORT,
			params,
		],
		mutationFn: (fileType: string) => exportFn(fileType as TExportFileTypes, requestParams),
		onSuccess: () => {
			params?.onSuccess?.();

			dispatch(setSnackbar({
				message: responseMessages.exportStartingNotificationSuccess,
				type: ESnackbarTypes.success,
			}));
		},
		onError: () => {
			dispatch(setSnackbar({
				message: responseMessages.error,
				type: ESnackbarTypes.error,
			}));
		},
	});

	return {
		exportKpiAlertsHistory: mutate,
		isLoading,
	};
}
